import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../../auth/useAuth';

interface Venue { name: string; address: string; pavilion: string; parkingNote: string; mapUrl: string; }
interface EventDoc {
  title: string; tagline: string; status: string;
  date: string; timeStart: string; timeEnd: string;
  venue: Venue; rsvpOpenDate: string; rsvpCloseDate: string; tshirtDeadline: string;
  showSchedule?: boolean;
  showGallery?: boolean;
  tshirtsEnabled?: boolean;
  golfEnabled?: boolean;
}

function fmtDate(iso: string) {
  return new Date(iso + 'T12:00:00').toLocaleDateString('en-US',
    { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}
function fmtShort(iso: string) {
  return new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

export function Home() {
  const { isSignedIn, getApiToken, signIn } = useAuth();
  const [event, setEvent]   = useState<EventDoc | null>(null);
  const [hasRsvp, setHasRsvp] = useState(false);

  // Public event doc (visibility flags, dates, venue, etc.)
  useEffect(() => {
    fetch('/api/public/event?year=2026').then(r => r.ok ? r.json() : null).then(setEvent).catch(() => null);
  }, []);

  // Whether the signed-in user already has an RSVP on file
  useEffect(() => {
    if (!isSignedIn) { setHasRsvp(false); return; }
    (async () => {
      try {
        const token = await getApiToken();
        const res = await fetch('/api/me/rsvp', { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) { setHasRsvp(false); return; }
        const data = await res.json();
        setHasRsvp(!!data);
      } catch { setHasRsvp(false); }
    })();
  }, [isSignedIn, getApiToken]);

  // Post-event "wrapped" mode (issue #57) — flipped by setting the event
  // status to 'completed' in /admin/event. Reverts automatically when the
  // next year's event goes live with a fresh status.
  const isWrapped  = event?.status === 'completed';
  const tagline    = isWrapped
    ? 'That’s a wrap on WICnic — thanks to everyone who came out!'
    : event?.tagline ?? 'Food, family, games, and a great afternoon.';
  const eventDate  = event?.date       ? fmtDate(event.date) : '—';
  const timeRange  = event             ? `${event.timeStart} – ${event.timeEnd} MDT` : '—';
  const venueName  = event?.venue.name ?? '—';
  const venueSub   = event             ? [event.venue.pavilion, event.venue.address].filter(Boolean).join(' · ') : '—';
  const venueUrl   = event?.venue.mapUrl ?? '#';
  const rsvpClose  = event?.rsvpCloseDate  ? fmtShort(event.rsvpCloseDate)  : '—';
  const shirtDeadline = event?.tshirtDeadline ? fmtShort(event.tshirtDeadline) : '—';
  const rsvpOpen   = event?.status === 'rsvp-open' || event?.status === 'announced';
  const showSchedule  = event?.showSchedule  !== false;
  const showGallery   = event?.showGallery   !== false;
  const tshirtsEnabled = event?.tshirtsEnabled !== false;
  const golfEnabled    = event?.golfEnabled    === true;
  const year = event?.date ? new Date(event.date + 'T12:00:00').getFullYear().toString() : '2026';

  // Smart RSVP button: shows "My RSVP" for signed-in users with an existing RSVP
  const rsvpHref  = hasRsvp ? '/my-rsvp' : '/rsvp';
  const rsvpLabel = hasRsvp ? 'My RSVP'  : 'RSVP Now';

  return (
    <>
      <section className="hero">
        <div className="hero__inner">
          <div className="hero__copy">
            <span className="eyebrow">— The Annual WIC Picnic</span>
            <h1 className="hero__title">
              WIC<span className="hero__title-accent">nic</span>
              <span className="hero__title-year">{year}</span>
            </h1>
            <p className="hero__subtitle">{tagline}</p>
            <div className="hero__actions">
              {rsvpOpen && <Link to={rsvpHref} className="btn">{rsvpLabel}</Link>}
              {isWrapped && showGallery && <Link to="/gallery" className="btn">View Photos</Link>}
              {/* Mobile hides the header auth control behind the hamburger
                  (issue #51) — give signed-out employees a visible way in.
                  Sign-in from Home lands on /my-rsvp (issue #49). */}
              {!isSignedIn && <button type="button" onClick={() => signIn()} className="btn btn--ghost">Employee Sign-In</button>}
              {!isWrapped && showSchedule && <Link to="/schedule" className="btn btn--ghost">View Schedule</Link>}
              {!isWrapped && showGallery && <Link to="/gallery" className="btn btn--ghost">Past Photos</Link>}
            </div>
            <p className="hero__subnote">
              {isWrapped
                ? 'Photos from the day are rolling into the gallery. Employees can sign in to browse and add their own shots.'
                : 'Open to employees, spouses, significant others, kids, retirees, and friends of the company. Anyone with the link can RSVP — guests confirm via email.'}
            </p>
          </div>

          <aside className="hero__card" aria-label="Event details">
            <div className="hero__card-rule" aria-hidden />
            <p className="eyebrow" style={{ marginBottom: 0 }}>{isWrapped ? 'Event Recap' : 'Event Details'}</p>
            <h3 className="hero__card-title">{isWrapped ? "That's a Wrap" : 'Save the Date'}</h3>
            {isWrapped ? (
              <dl className="hero__details">
                <div>
                  <dt>When It Happened</dt>
                  <dd>{eventDate}<span>{timeRange}</span></dd>
                </div>
                <div>
                  <dt>Where</dt>
                  <dd>
                    <a href={venueUrl} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>{venueName}</a>
                    <span>{venueSub}</span>
                  </dd>
                </div>
                <div>
                  <dt>Photos</dt>
                  <dd>Gallery is open<span>Employees can sign in to browse and upload</span></dd>
                </div>
                <div>
                  <dt>Next Up</dt>
                  <dd>WICnic {Number(year) + 1}</dd>
                </div>
              </dl>
            ) : (
              <dl className="hero__details">
                <div>
                  <dt>When</dt>
                  <dd>{eventDate}<span>{timeRange}</span></dd>
                </div>
                <div>
                  <dt>Where</dt>
                  <dd>
                    <a href={venueUrl} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>{venueName}</a>
                    <span>{venueSub}</span>
                  </dd>
                </div>
                <div>
                  <dt>RSVP Deadline</dt>
                  <dd>
                    {rsvpClose}
                    {tshirtsEnabled && <span>T-shirt orders close {shirtDeadline}</span>}
                  </dd>
                </div>
                <div>
                  <dt>Bring</dt>
                  <dd>Sunscreen, a competitive spirit<span>Food &amp; drinks provided</span></dd>
                </div>
              </dl>
            )}
          </aside>
        </div>
      </section>

      <div className="page-divider" aria-hidden />

      {/* No mid-page section when wrapped — Option C's photo-highlights strip
          (issue #59) will slot in here once photos can be surfaced publicly. */}
      {!isWrapped && (
        <section className="section section-bg">
          <div className="container">
            <span className="eyebrow">— What's on the Docket</span>
            <h2 style={{ marginBottom: '2rem' }}>An afternoon worth showing up for</h2>
            <div className="cards">
              <Card title="The Food"  body="Georgia Boys BBQ catering — brisket, pulled pork, chicken, all the sides." />
              <Card
                title="The Games"
                body="Cornhole, volleyball, lawn games, and kids activities. Plus a morning golf tournament at Riverdale Dunes — 8:30 AM shotgun start."
                linkTo={golfEnabled ? "/golf" : undefined}
                linkLabel="Golf details"
              />
              <Card title="The Photos" body="Group photo opportunity, plus candid coverage all afternoon. Photos posted to the gallery after the event." />
            </div>
          </div>
        </section>
      )}

      <section className="section">
        <div className="container final-cta">
          {isWrapped ? (
            <>
              <span className="eyebrow">Same Time Next Year</span>
              <h2>See you at WICnic {Number(year) + 1}.</h2>
              {showGallery && <Link to="/gallery" className="btn">View Photos</Link>}
            </>
          ) : (
            <>
              <span className="eyebrow">— Don't Be the Person We're Saving a Plate For</span>
              <h2>Headcount closes {rsvpClose}.</h2>
              {rsvpOpen && <Link to={rsvpHref} className="btn">{rsvpLabel}</Link>}
              {!rsvpOpen && event?.status === 'rsvp-closed' && (
                <p style={{ fontFamily: 'var(--font-body)', color: 'var(--muted)' }}>RSVPs are now closed.</p>
              )}
            </>
          )}
        </div>
      </section>

      <style>{`
        .hero {
          position: relative;
          padding: var(--section) var(--gutter) calc(var(--section) - 1rem);
          background: var(--white);
          overflow: hidden;
        }
        .hero::before {
          content: "";
          position: absolute;
          top: 0; left: 0;
          width: 60%;
          height: 6px;
          background: var(--red);
        }
        .hero__inner {
          max-width: 1400px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: minmax(0, 1.4fr) minmax(280px, 1fr);
          gap: 4rem;
          align-items: end;
        }
        .hero__title {
          font-size: clamp(4rem, 12vw, 11rem);
          line-height: 0.9;
          letter-spacing: -0.02em;
          margin: 0.25em 0 0.4em 0;
          color: var(--black);
        }
        .hero__title-accent { color: var(--red); }
        .hero__title-year {
          display: block;
          font-size: 0.32em;
          letter-spacing: 0.4em;
          color: var(--mid);
          margin-top: 0.4em;
          font-weight: 500;
        }
        .hero__subtitle {
          font-family: var(--font-sub);
          font-weight: 500;
          font-size: clamp(1.1rem, 1.4vw, 1.4rem);
          color: var(--charcoal);
          max-width: 36ch;
          margin: 0 0 2rem 0;
          line-height: 1.4;
        }
        .hero__actions { display: flex; gap: 1rem; flex-wrap: wrap; margin-bottom: 1.5rem; }
        .hero__subnote { font-size: 0.9rem; color: var(--muted); max-width: 50ch; margin: 0; }
        .hero__card { background: var(--bg); padding: 2rem 2rem 2.25rem; position: relative; }
        .hero__card-rule { position: absolute; left: 0; top: 0; width: 4px; height: 100%; background: var(--red); }
        .hero__card-title { margin: 0.4rem 0 1.25rem; font-size: 1.4rem; }
        .hero__details { margin: 0; padding: 0; }
        .hero__details > div { padding: 0.9rem 0; border-top: 1px solid var(--border); }
        .hero__details > div:last-child { padding-bottom: 0; }
        .hero__details dt {
          font-family: var(--font-display);
          font-weight: 500;
          font-size: 0.7rem;
          letter-spacing: 0.3em;
          text-transform: uppercase;
          color: var(--red);
          margin-bottom: 0.3rem;
        }
        .hero__details dd { margin: 0; font-family: var(--font-sub); font-weight: 500; color: var(--black); font-size: 1rem; line-height: 1.4; }
        .hero__details dd span { display: block; color: var(--mid); font-weight: 400; font-size: 0.85rem; margin-top: 0.15rem; }
        .hero__details a:hover { color: var(--red); }
        .page-divider { height: 4px; background: var(--black); width: 100%; }
        .cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 1.5rem; }
        .final-cta { text-align: center; }
        .final-cta h2 { margin-bottom: 2rem; }
        @media (max-width: 980px) {
          .hero__inner { grid-template-columns: 1fr; gap: 2.5rem; }
          .hero__title { font-size: clamp(3.5rem, 18vw, 6rem); }
        }
      `}</style>
    </>
  );
}

function Card({ title, body, linkTo, linkLabel }: { title: string; body: string; linkTo?: string; linkLabel?: string }) {
  return (
    <article className="card-tile">
      <h3>{title}</h3>
      <p>{body}</p>
      {linkTo && <Link to={linkTo} className="card-tile__link">{linkLabel ?? 'Learn more'} →</Link>}
      <style>{`
        .card-tile { background: var(--white); padding: 1.75rem; border-top: 4px solid var(--red); }
        .card-tile h3 { margin: 0 0 0.75rem 0; font-size: 1.25rem; }
        .card-tile p { margin: 0; color: var(--steel); line-height: 1.6; }
        .card-tile__link {
          display: inline-block; margin-top: 1rem;
          font-family: var(--font-display); font-weight: 600; font-size: 0.78rem;
          letter-spacing: 0.1em; text-transform: uppercase; color: var(--red);
          text-decoration: none;
        }
        .card-tile__link:hover { color: var(--red-dark); text-decoration: underline; }
      `}</style>
    </article>
  );
}
